import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { CustomUser } from 'api/models/CustomUser';
import { ApiService } from 'api/services/ApiService';
import { FilterTypeSelection, MODEL_FILTER_TYPES, ModelFilterType, ModelsStatusOption } from 'dashboard409a/components/ModelsTable/ModelsTable.types';
import { ViewUserId } from 'dashboard409a/pages/Workboard/ModelViewsSelector/ModelViewsSelector.props';
import { SelectedUserWithGroup } from 'dashboard409a/pages/Workboard/UserGroups/UserGroups.types';
import { useDebouncedValue } from 'hooks';
import { dateToISOStringKey, normalizeDate } from 'utilities/dateUtils';
import { displayErrorMessage } from 'utilities/displayErrorMessage';

const DEFAULT_FILTER_TYPE_SELECTION: FilterTypeSelection = {
  current: true,
  past: false,
  unassigned: false,
  all: false,
};

// Helper to convert FilterTypeSelection to backend ModelFilterType
const getBackendFilterType = (selection: FilterTypeSelection): ModelFilterType => {
  const { current, past, unassigned } = selection;

  // If all selected, return 'all'
  if (current && past && unassigned) {
    return MODEL_FILTER_TYPES.ALL;
  }
  // If none selected, still return 'all' but client will filter to empty
  if (!current && !past && !unassigned) {
    return MODEL_FILTER_TYPES.ALL;
  }
  // Only one selected
  if (current && !past && !unassigned) return MODEL_FILTER_TYPES.CURRENT;
  if (!current && past && !unassigned) return MODEL_FILTER_TYPES.PAST;
  if (!current && !past && unassigned) return MODEL_FILTER_TYPES.UNASSIGNED;

  // Partial combinations -> use 'all' and filter on client
  return MODEL_FILTER_TYPES.ALL;
};

// Helper to convert legacy filterType string to FilterTypeSelection
const filterTypeToSelection = (filterType: ModelFilterType): FilterTypeSelection => {
  switch (filterType) {
    case MODEL_FILTER_TYPES.CURRENT:
      return { current: true, past: false, unassigned: false, all: false };
    case MODEL_FILTER_TYPES.PAST:
      return { current: false, past: true, unassigned: false, all: false };
    case MODEL_FILTER_TYPES.UNASSIGNED:
      return { current: false, past: false, unassigned: true, all: false };
    case MODEL_FILTER_TYPES.ALL:
      return { current: true, past: true, unassigned: true, all: true };
    default:
      return DEFAULT_FILTER_TYPE_SELECTION;
  }
};

type UserRole = 'preparer' | 'reviewer';

// Helper function to sort and merge users with selected users
const sortAndMergeUsers = (usersData: CustomUser[] | undefined, selectedUsers: CustomUser[]): CustomUser[] => {
  const users = usersData || [];
  const searchResultIds = new Set(users.map(u => u.id).filter(Boolean));

  const selectedUsersNotInResults = selectedUsers.filter(
    selected => selected.id && !searchResultIds.has(selected.id)
  );

  const allUsers = [...selectedUsersNotInResults, ...users];

  const uniqueUsers = Array.from(
    new Map(allUsers.map(user => [user.id, user])).values()
  );

  return uniqueUsers.sort((a, b) => {
    const aSelected = selectedUsers.some(selected => selected.id === a.id);
    const bSelected = selectedUsers.some(selected => selected.id === b.id);

    if (aSelected && !bSelected) {
      return -1;
    }
    if (!aSelected && bSelected) {
      return 1;
    }
    return 0;
  });
};

const filterUsersBySearch = (users: CustomUser[], searchInput: string): CustomUser[] => {
  const term = searchInput.trim().toLowerCase();
  if (!term) {
    return users;
  }

  return users.filter(user => {
    const fullName = [user.first_name, user.last_name].filter(Boolean).join(' ');
    return [fullName, user.email, user.username].some(value =>
      (value || '').toLowerCase().includes(term)
    );
  });
};

export interface UseModelFiltersProps {
  onFiltersChange: (filterState: {
    searchParam: string;
    searchStatus: string[];
    fromDate: Date | null;
    toDate: Date | null;
    preparerIds: number[];
    reviewerIds: number[];
    filterType?: ModelFilterType;
    filterTypeSelection?: FilterTypeSelection;
  }) => void;
  viewToApply?: {
    preparerIds: ViewUserId[];
    reviewerIds: ViewUserId[];
    searchStatus: string | string[];
    fromDate: Date | string | null;
    toDate: Date | string | null;
    filterType?: ModelFilterType;
    filterTypeSelection?: FilterTypeSelection;
  } | null;
  statusOptions?: ModelsStatusOption[];
}

export const useModelFilters = ({ onFiltersChange, viewToApply, statusOptions = [] }: UseModelFiltersProps) => {
  const [searchParam, setSearchParam] = useState('');
  const [selectedStatuses, setSelectedStatuses] = useState<ModelsStatusOption[]>([]);
  const [filterType, setFilterType] = useState<ModelFilterType>(MODEL_FILTER_TYPES.CURRENT);
  const [filterTypeSelection, setFilterTypeSelection] = useState<FilterTypeSelection>(DEFAULT_FILTER_TYPE_SELECTION);
  const [fromDueDate, setFromDueDate] = useState<Date | null>(null);
  const [toDueDate, setToDueDate] = useState<Date | null>(null);
  const [selectedPreparers, setSelectedPreparers] = useState<SelectedUserWithGroup[]>([]);
  const [selectedReviewers, setSelectedReviewers] = useState<SelectedUserWithGroup[]>([]);
  const [preparerSearchInput, setPreparerSearchInput] = useState('');
  const [reviewerSearchInput, setReviewerSearchInput] = useState('');
  const [statusSearchInput, setStatusSearchInput] = useState('');

  const debouncedSearchParam = useDebouncedValue(searchParam, 500);
  const lastAppliedViewRef = useRef<string | null>(null);

  // Fetch all staff users once and reuse for both preparer and reviewer selectors
  const { data: staffUsers = [], isLoading: isLoadingStaff } = useQuery({
    queryKey: ['users-list', 'staff'],
    queryFn: async () => {
      const response = await ApiService.apiUsersStaffList();
      // We assume that users with "+" in their email are test users so we filter them out
      const users = response.filter(user => !user.email?.includes('+'));
      return (users as CustomUser[]) || [];
    },
  });

  const sortedPreparers = useMemo(
    () => filterUsersBySearch(sortAndMergeUsers(staffUsers, selectedPreparers), preparerSearchInput),
    [staffUsers, selectedPreparers, preparerSearchInput]
  );

  const sortedReviewers = useMemo(
    () => filterUsersBySearch(sortAndMergeUsers(staffUsers, selectedReviewers), reviewerSearchInput),
    [staffUsers, selectedReviewers, reviewerSearchInput]
  );

  useEffect(() => {
    if (!viewToApply) {
      lastAppliedViewRef.current = null;
      return;
    }

    // Create a unique key for this view to prevent re-applying the same view
    // Sort arrays for comparison (numeric sort for IDs)
    const sortedPreparerIds = [...viewToApply.preparerIds].map(p => p.userId).sort((a, b) => a - b);
    const sortedReviewerIds = [...viewToApply.reviewerIds].map(r => r.userId).sort((a, b) => a - b);
    let searchStatusArray: string[] = [];
    if (Array.isArray(viewToApply.searchStatus)) {
      searchStatusArray = [...viewToApply.searchStatus].sort((a, b) => a.localeCompare(b));
    } else if (viewToApply.searchStatus) {
      searchStatusArray = [viewToApply.searchStatus];
    }
    const viewKey = JSON.stringify({
      preparerIds: sortedPreparerIds,
      reviewerIds: sortedReviewerIds,
      searchStatus: searchStatusArray,
      fromDate: dateToISOStringKey(viewToApply.fromDate),
      toDate: dateToISOStringKey(viewToApply.toDate),
      filterType: viewToApply.filterType ?? MODEL_FILTER_TYPES.CURRENT,
    });

    // Skip if we've already applied this exact view
    if (lastAppliedViewRef.current === viewKey) {
      return;
    }

    // If the view has preparer/reviewer IDs but staff users haven't loaded yet,
    // defer the entire view application to avoid triggering onFiltersChange with
    // preparerIds=[] — which would override the correct initialization done by the
    // parent component and cause the models request to be aborted and restarted.
    const hasPreparersOrReviewers =
      viewToApply.preparerIds.length > 0 || viewToApply.reviewerIds.length > 0;
    if (hasPreparersOrReviewers && staffUsers.length === 0) {
      return;
    }

    if (staffUsers.length > 0) {
      const preparersFromIds = viewToApply.preparerIds
        .map(({ userId, groupId }) => {
          const user = staffUsers.find(u => u.id === userId);
          if (!user) return undefined;
          return { ...user, fromGroupId: groupId ?? undefined } as SelectedUserWithGroup;
        })
        .filter((user): user is SelectedUserWithGroup => user !== undefined);
      const reviewersFromIds = viewToApply.reviewerIds
        .map(({ userId, groupId }) => {
          const user = staffUsers.find(u => u.id === userId);
          if (!user) return undefined;
          return { ...user, fromGroupId: groupId ?? undefined } as SelectedUserWithGroup;
        })
        .filter((user): user is SelectedUserWithGroup => user !== undefined);

      setSelectedPreparers(preparersFromIds);
      setSelectedReviewers(reviewersFromIds);
      lastAppliedViewRef.current = viewKey;
    }

    // Apply status from the view
    if (viewToApply.searchStatus) {
      let statusArray: string[] = [];
      if (Array.isArray(viewToApply.searchStatus)) {
        statusArray = viewToApply.searchStatus;
      } else {
        statusArray = [viewToApply.searchStatus];
      }
      const statusesFromValues = statusArray
        .map(value => statusOptions.find(opt => opt.value === value))
        .filter((opt): opt is ModelsStatusOption => opt !== undefined);
      setSelectedStatuses(statusesFromValues);
    } else {
      setSelectedStatuses([]);
    }

    // Apply dates from the view
    if (viewToApply.fromDate !== undefined) {
      setFromDueDate(normalizeDate(viewToApply.fromDate));
    }
    if (viewToApply.toDate !== undefined) {
      setToDueDate(normalizeDate(viewToApply.toDate));
    }

    // Apply filterType from the view (default to 'current' if not specified)
    const appliedFilterType = viewToApply.filterType ?? MODEL_FILTER_TYPES.CURRENT;
    setFilterType(appliedFilterType);

    // Apply filterTypeSelection from the view or convert from filterType
    if (viewToApply.filterTypeSelection) {
      setFilterTypeSelection(viewToApply.filterTypeSelection);
    } else {
      setFilterTypeSelection(filterTypeToSelection(appliedFilterType));
    }
  }, [viewToApply, staffUsers, statusOptions]);

  useEffect(() => {
    const preparerIds = selectedPreparers.map(user => user.id).filter((id): id is number => id !== undefined);
    const reviewerIds = selectedReviewers.map(user => user.id).filter((id): id is number => id !== undefined);
    const searchStatusValues = selectedStatuses.map(status => status.value);

    // Use getBackendFilterType to determine the filterType to send to the backend
    const backendFilterType = getBackendFilterType(filterTypeSelection);

    onFiltersChange({
      searchParam: debouncedSearchParam,
      searchStatus: searchStatusValues,
      fromDate: fromDueDate,
      toDate: toDueDate,
      preparerIds,
      reviewerIds,
      filterType: backendFilterType,
      filterTypeSelection,
    });
  }, [
    debouncedSearchParam,
    selectedStatuses,
    fromDueDate,
    toDueDate,
    selectedPreparers,
    selectedReviewers,
    filterTypeSelection,
    onFiltersChange,
  ]);

  const handleSearchParamChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchParam(event.target.value);
  };

  const handleStatusesChange = (_event: unknown, newValue: ModelsStatusOption[]) => {
    setSelectedStatuses(newValue);
  };

  const handleFromDueDateChange = (date: Date | null) => {
    setFromDueDate(date);
  };

  const handleToDueDateChange = (date: Date | null) => {
    if (fromDueDate && date && fromDueDate > date) {
      displayErrorMessage('From Valuation Date must be before To Valuation Date');
      return;
    }
    setToDueDate(date);
  };

  const handleUsersChange = (role: UserRole) => (newValue: SelectedUserWithGroup[]) => {
    if (role === 'preparer') {
      setSelectedPreparers(newValue);
    } else {
      setSelectedReviewers(newValue);
    }
  };

  const handleFilterTypeChange = (event: { target: { value: unknown } }) => {
    const newFilterType = event.target.value as ModelFilterType;
    setFilterType(newFilterType);
    setFilterTypeSelection(filterTypeToSelection(newFilterType));
  };

  const handleFilterTypeToggle = useCallback((type: keyof FilterTypeSelection) => () => {
    setFilterTypeSelection(prev => {
      if (type === 'all') {
        // If toggling All, set all to the new value
        const newValue = !prev.all;
        const newSelection = {
          current: newValue,
          past: newValue,
          unassigned: newValue,
          all: newValue,
        };
        // Update filterType based on the new selection
        setFilterType(getBackendFilterType(newSelection));
        return newSelection;
      }
      // Toggle the specific type
      const newSelection = { ...prev, [type]: !prev[type] };
      // Auto-set All if all three are selected
      newSelection.all = newSelection.current && newSelection.past && newSelection.unassigned;
      // Update filterType based on the new selection
      setFilterType(getBackendFilterType(newSelection));
      return newSelection;
    });
  }, []);

  const cleanFilters = () => {
    setSearchParam('');
    setSelectedStatuses([]);
    setFilterType(MODEL_FILTER_TYPES.CURRENT);
    setFilterTypeSelection(DEFAULT_FILTER_TYPE_SELECTION);
    setFromDueDate(null);
    setToDueDate(null);
    setSelectedPreparers([]);
    setSelectedReviewers([]);
    setPreparerSearchInput('');
    setReviewerSearchInput('');
    setStatusSearchInput('');
  };

  // Filter and sort status options
  const sortedStatuses = useMemo(() => {
    const term = statusSearchInput.trim().toLowerCase();
    if (!term) {
      return statusOptions;
    }
    return statusOptions.filter(option =>
      option.label.toLowerCase().includes(term) || option.value.toLowerCase().includes(term)
    );
  }, [statusOptions, statusSearchInput]);

  // Check if only Unassigned is selected (for disabling Reviewers/Preparers)
  const isOnlyUnassignedSelected = filterTypeSelection.unassigned &&
    !filterTypeSelection.current &&
    !filterTypeSelection.past;

  return {
    // State
    searchParam,
    selectedStatuses,
    filterType,
    filterTypeSelection,
    fromDueDate,
    toDueDate,
    selectedPreparers,
    selectedReviewers,
    preparerSearchInput,
    reviewerSearchInput,
    statusSearchInput,
    sortedPreparers,
    sortedReviewers,
    sortedStatuses,
    isLoadingPreparers: isLoadingStaff,
    isLoadingReviewers: isLoadingStaff,
    isOnlyUnassignedSelected,
    // Handlers
    handleSearchParamChange,
    handleStatusesChange,
    handleFilterTypeChange,
    handleFilterTypeToggle,
    handleFromDueDateChange,
    handleToDueDateChange,
    handlePreparersChange: handleUsersChange('preparer'),
    handleReviewersChange: handleUsersChange('reviewer'),
    setPreparerSearchInput,
    setReviewerSearchInput,
    setStatusSearchInput,
    cleanFilters,
  };
};

